import { Link } from "react-router-dom";
import { useT } from "../i18n/I18nProvider";
import { ChevronLeft } from "lucide-react";
import {
  PL,
  ContentSection,
  AutoTOC,
  KeyPointsBlock,
  Sources,
  Warn,
} from "../components/contentTemplate";

// Section order mirrors the 10 H2 headings of the article body.
// Anchor IDs are short, stable, kebab-case so they survive copy/paste sharing.
const SECTIONS = [
  { id: "loi-bref",              title: "La loi en bref" },
  { id: "changements",           title: "Ce qui change concrètement" },
  { id: "pourquoi",              title: "Pourquoi cette loi, et pourquoi maintenant" },
  { id: "arbitrage",             title: "La logique de l'arbitrage" },
  { id: "soutiens-oppositions",  title: "Qui soutient, qui s'y oppose" },
  { id: "contexte-politique",    title: "Le contexte politique : une cohabitation tendue" },
  { id: "contexte-geopolitique", title: "Le contexte géopolitique" },
  { id: "consequences",          title: "Les conséquences déjà visibles" },
  { id: "pour-vous",             title: "Ce que cela change pour vous" },
  { id: "et-apres",              title: "Et après" },
];

const SOURCES = [
  { url: "https://www.gov.pl/web/family/thats-a-given-labour-market-laws-will-come-into-effect-on-1-june-2025",
    label: "Ministère de la Famille, du Travail et de la Politique sociale (gov.pl), entrée en vigueur des lois sur le marché du travail au 1er juin 2025",
    description: "Source officielle. Elle confirme la date d'entrée en vigueur, l'inscription dans le Plan national de relance et les objectifs affichés, protéger les travailleurs polonais et contrer les permis frauduleux servant à entrer dans Schengen." },
  { url: "https://kpmg.com/xx/en/our-insights/gms-flash-alert/flash-alert-2025-140.html",
    label: "KPMG, Global Mobility Services Flash Alert sur la nouvelle loi",
    description: "Donne le nom exact de la loi du 20 mars 2025, son remplacement de la loi de 2004, l'approche double de simplification et de garde-fous ainsi que l'obligation de créer un compte sur praca.gov.pl." },
  { url: "https://www.ibanet.org/new-rules-for-hiring-foreigners-in-Poland",
    label: "International Bar Association, nouvelles règles d'embauche des étrangers",
    description: "Établit la suppression du test du marché du travail, les listes locales de métiers protégés, l'interdiction de travailler pour certains types de visas, la règle sur les visas Schengen étrangers, le relèvement des amendes et l'obligation de transmettre le contrat." },
  { url: "https://www.tgc.eu/en/publications/employing-foreigners-in-poland-changes-in-regulations-from-1-june-2025/",
    label: "TGC Corporate Lawyers, changements au 1er juin 2025",
    description: "Confirme l'abrogation de l'informacja starosty et précise que la loi ne s'applique ni aux citoyens de l'UE et de l'EEE, ni aux Britanniques couverts par l'accord de retrait." },
  { url: "https://www.accace.com/changes-in-polish-immigration-law/",
    label: "Accace, changements du droit migratoire polonais",
    description: "Détaille le paquet de quatre lois, leurs références au Journal officiel, la dématérialisation et la suppression du test du marché du travail." },
  { url: "https://www.prorelo.com/2025/04/17/new-changes-employing-foreigners-poland-2025/",
    label: "ProRelo, nouvelles règles d'emploi des étrangers en 2025",
    description: "Retrace le parcours législatif, vote du Sénat le 13 mars 2025, adoption par le Sejm le 20 mars 2025, signature présidentielle le 10 avril 2025 et entrée en vigueur le premier jour du mois suivant 14 jours après publication." },
  { url: "https://psz.praca.gov.pl/documents/10828/183932/Informacje+o+zatrudnieniu+cudzoziemc%C3%B3w+w+Polsce+-+I+p%C3%B3%C5%82rocze++2025.pdf",
    label: "Ministère du Travail, rapport sur l'emploi des étrangers au premier semestre 2025 (PDF, praca.gov.pl)",
    description: "Source officielle. Elle établit la baisse des permis de travail (près de 145 000 au premier semestre 2025, soit 17 % de moins), la hausse des refus (6 000, soit près de 30 % de plus), le recul des déclarations et la répartition par nationalité. Elle situe aussi la loi dans la stratégie migratoire 2025-2030." },
  { url: "https://pro.rp.pl/raporty-ekonomiczne/art43750451-cudzoziemcy-wypelniaja-luki-na-rynku-pracy-padl-nowy-rekord",
    label: "rp.pl, nouveau record d'étrangers dans les registres de la ZUS",
    description: "Donne le chiffre de fin 2025, près de 1,29 million d'étrangers travaillant légalement, en hausse de 8 % sur un an, d'après les données de la sécurité sociale." },
  { url: "https://www.money.pl/gospodarka/liczba-cudzoziemcow-w-polsce-zus-pokazal-najnowsze-dane-7242912391322592a.html",
    label: "money.pl, dernières données de la ZUS sur les étrangers",
    description: "Établit qu'à fin juillet 2025 les étrangers représentaient 6,7 % des personnes en emploi et que les Ukrainiens, environ 741 000, formaient 67 % des travailleurs étrangers." },
  { url: "https://www.money.pl/gospodarka/cudzoziemcy-w-polsce-eksperci-pokazuja-wplyw-na-gospodarke-7289936478451872a.html",
    label: "money.pl, impact économique des étrangers (rapport ISP, Deloitte et Ipsos)",
    description: "Estime la population étrangère entre deux et deux millions et demi de personnes, chiffre l'apport au PIB 2025 entre 200 et 416 milliards de zlotys et cite la projection d'une main-d'œuvre en recul de 2,1 millions d'ici 2035." },
  { url: "https://gdansk.pip.gov.pl/aktualnosci/od-stycznia-2026-r-wynagrodzenie-minimalne-w-gore",
    label: "Inspection du travail de Gdańsk (PIP), salaire minimum au 1er janvier 2026",
    description: "Source officielle. Elle fixe le salaire minimum 2026 à 4 806 PLN bruts par mois et le taux horaire minimum à 31,40 PLN bruts." },
  { url: "https://en.wikipedia.org/wiki/Karol_Nawrocki",
    label: "Karol Nawrocki (Wikipédia)",
    description: "Établit qu'il est président de la Pologne depuis le 6 août 2025, qu'il a succédé à Andrzej Duda et qu'il cohabite avec le Premier ministre Donald Tusk." },
  { url: "https://europeannewsroom.com/new-polish-president-strikes-combative-anti-eu-anti-migration-tone/",
    label: "European Newsroom, le nouveau président adopte un ton anti-UE et anti-immigration",
    description: "Décrit la prise de fonction de Nawrocki, sa ligne sur l'immigration et l'euro ainsi que son pouvoir de veto." },
  { url: "https://www.globalsecurity.org/wmd/library/news/ukraine/2025/08/ukraine-250827-rferl03.htm",
    label: "RFE/RL via GlobalSecurity, veto présidentiel sur l'aide aux réfugiés ukrainiens",
    description: "Établit que Nawrocki a opposé son veto, le 26 août 2025, à une loi sur l'aide aux réfugiés ukrainiens." },
  { url: "https://verfassungsblog.de/what-nawrockis-victory-means-for-europe/",
    label: "Verfassungsblog, ce que la victoire de Nawrocki signifie pour l'Europe",
    description: "Analyse le pouvoir de veto présidentiel, l'euroscepticisme de Nawrocki et le fait que, sur l'immigration, sa position durcit le ton sans s'opposer frontalement à l'approche du gouvernement." },
];

// Shared classes for inline internal links in the body.
const INTERNAL_LINK = "text-[#0056D2] hover:underline";

export default function BlogPostReformeTravail() {
  const { lp } = useT();

  return (
    <article
      className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16"
      data-testid="blog-post-reforme-travail"
    >
      <Link
        to={lp("/blog")}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#0056D2] mb-6"
        data-testid="blog-post-back"
      >
        <ChevronLeft className="w-4 h-4" />
        Retour au blog
      </Link>

      {/* Hero */}
      <header className="mb-10" data-testid="blog-post-hero">
        <span className="hej-chip" data-testid="blog-post-chip">
          <span className="w-1.5 h-1.5 rounded-full bg-[#DC143C]"></span>
          Mis à jour en juin 2026
        </span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-4">
          Travailler en Pologne après la réforme de 2025 : ce qui change pour les ressortissants de pays tiers
        </h1>
      </header>

      {/* Chapeau d'introduction */}
      <section className="space-y-4 text-slate-700 leading-relaxed text-lg mb-12" data-testid="blog-post-lede">
        <p>
          Depuis le 1er juin 2025, la Pologne recrute ses travailleurs étrangers selon des règles neuves. Un point décisif avant tout
          le reste. Cette réforme ne vise que les ressortissants de pays tiers. Les citoyens de l'UE et de l'EEE, les Suisses et les
          Britanniques couverts par l'accord de retrait gardent leur libre accès au marché du travail et ne sont pas concernés. Pour
          tous les autres, une loi adoptée au printemps 2025 a remplacé un texte vieux de plus de vingt ans. Elle simplifie l'embauche
          d'un côté et resserre les contrôles de l'autre. C'est ce double mouvement qu'il faut saisir, parce qu'il en dit long sur la
          trajectoire du pays.
        </p>
        <p>
          Cet article explique ce que la réforme change concrètement, d'où elle vient, qui la soutient, qui la critique et quelles
          conséquences elle produit déjà. Pour les démarches pas à pas comme le permis, le visa et la carte de séjour, voir notre
          page <Link to={lp("/demarches")} className={INTERNAL_LINK}>Démarches</Link>. Pour les salaires et le pouvoir d'achat, voir{" "}
          <Link to={lp("/cout-de-la-vie")} className={INTERNAL_LINK}>Coût de la vie</Link>.
        </p>
      </section>

      {/* Sommaire auto-généré */}
      <AutoTOC sections={SECTIONS} testid="blog-post-toc" />

      {/* À retenir */}
      <KeyPointsBlock testid="blog-post-key-points">
        <p>
          Moins de bureaucratie au départ, mais des dossiers plus exigeants. Côté employeur, la réforme accélère l'embauche. Côté
          travailleur hors UE, elle impose un dossier solide et un salaire au moins égal au minimum légal. Et un rappel utile, rien
          de tout cela ne concerne les citoyens de l'UE, de l'EEE et de Suisse, ni les Britanniques couverts par l'accord de retrait.
        </p>
      </KeyPointsBlock>

      {/* Infographie résumé visuel */}
      <figure className="mb-12" data-testid="blog-post-infographic">
        <img
          src="/images/blog/reforme-travail-pologne-2025.png"
          alt="Infographie résumant la réforme du travail en Pologne de 2025 pour les ressortissants de pays tiers : idée centrale, public concerné, situation avant et maintenant, trois garde-fous, nouvelle procédure numérique et chiffres clés."
          loading="lazy"
          width="1024"
          height="1535"
          className="w-full h-auto rounded-2xl border border-slate-200 shadow-sm"
        />
        <figcaption className="mt-3 text-sm text-slate-500 text-center italic">
          Réforme du travail en Pologne 2025, l'essentiel en un coup d'œil. Analyse de juin 2026.
        </figcaption>
      </figure>

      {/* Sections */}
      <ContentSection id="loi-bref" n={1} title="La loi en bref">
        <p>
          Le texte porte un nom à rallonge,{" "}
          <PL>ustawa o warunkach dopuszczalności powierzania pracy cudzoziemcom na terytorium Rzeczypospolitej Polskiej</PL>, soit la
          loi du 20 mars 2025 sur les conditions d'admissibilité du travail confié aux étrangers. Sa référence officielle est Dz.U.
          2025 poz. 621. Elle est entrée en vigueur le 1er juin 2025 et remplace la loi de 2004 sur la promotion de l'emploi. Elle
          fait partie d'un paquet de quatre textes qui mettent en œuvre des jalons du Plan national de relance financé par l'Union
          européenne.
        </p>
      </ContentSection>

      <ContentSection id="changements" n={2} title="Ce qui change concrètement">
        <p>
          Le changement le plus commenté est la fin du test du marché du travail. Avant, l'employeur devait d'abord obtenir une
          attestation du chef de district, l'<PL>informacja starosty</PL>, confirmant qu'aucun candidat polonais ou européen n'était
          disponible pour le poste. Cette étape allongeait les procédures de plusieurs semaines. Elle disparaît. Désormais l'employeur
          dépose sa demande de permis directement auprès du voïvode.
        </p>
        <p>
          À la place, l'État garde la main par un autre levier. Les autorités locales peuvent établir des listes de métiers fermés au
          recrutement étranger, pour protéger l'emploi national là où le chômage le justifie. Le contrôle n'a pas disparu. Il a
          changé de forme.
        </p>
        <p>
          La procédure devient entièrement numérique. Les employeurs créent un compte sur la plateforme{" "}
          <PL>praca.gov.pl</PL> et y transmettent une copie du contrat avant que l'étranger ne commence à travailler. En parallèle,
          la loi durcit les sanctions. Les amendes pour emploi illégal augmentent, les motifs de refus s'élargissent et les
          inspections peuvent être menées sans préavis.
        </p>
        <p>
          La réforme ferme aussi certaines portes. Les titulaires de visas délivrés pour rendre visite à la famille, pour des études
          non universitaires, pour un traitement médical ou pour d'« autres raisons » ne peuvent plus travailler. De même, un visa
          Schengen délivré par un autre pays ne donne pas accès à l'emploi en Pologne, sauf si la personne détient un titre de séjour
          de ce pays.
        </p>
      </ContentSection>

      <ContentSection id="pourquoi" n={3} title="Pourquoi cette loi, et pourquoi maintenant">
        <p>
          La loi précédente datait de 2004. À l'époque, la Pologne sortait d'années de chômage massif et cherchait surtout à protéger
          ses propres travailleurs. Vingt ans plus tard, le pays a changé de visage. Il est devenu une terre d'immigration confrontée
          à un manque de bras.
        </p>
        <p>
          Le moteur est démographique. La population vieillit vite et la main-d'œuvre se réduit. Selon l'Institut polonais de
          l'économie, le nombre de travailleurs pourrait diminuer de 2,1 millions d'ici 2035, soit près de 13 % de l'emploi actuel.
          Sans apport extérieur, des secteurs entiers comme la logistique, le soin aux personnes ou la construction tourneraient au
          ralenti. La réforme répond à ce besoin, tout en s'inscrivant dans les engagements de modernisation pris auprès de l'Union
          au titre du Plan national de relance.
        </p>
      </ContentSection>

      <ContentSection id="arbitrage" n={4} title="La logique de l'arbitrage">
        <p>
          L'État polonais n'a pas ouvert ses portes sans condition. Il a cherché un équilibre entre attractivité et contrôle. Trois
          garde-fous structurent ce compromis.
        </p>
        <p>
          Le premier est l'égalité de rémunération. Un travailleur étranger doit toucher au moins le salaire minimum légal, fixé à
          4 806 PLN bruts par mois en 2026, et pas moins qu'un travailleur polonais au même poste. Cette règle empêche de tirer les
          salaires vers le bas par une main-d'œuvre bon marché. Pour replacer ce montant dans un budget réel, voir{" "}
          <Link to={lp("/cout-de-la-vie")} className={INTERNAL_LINK}>Coût de la vie</Link>.
        </p>
        <p>
          Le deuxième est la lutte contre la fraude. L'un des objectifs affichés est de couper court aux faux permis de travail
          servant uniquement de passerelle vers l'espace Schengen. D'où l'élargissement des motifs de refus et le renforcement des
          contrôles.
        </p>
        <p>
          Le troisième est le pilotage local. Les listes de métiers protégés permettent de freiner le recrutement étranger dans une
          région ou un secteur précis si la situation de l'emploi se dégrade. Ce n'est pas un système de quotas nationaux,
          contrairement à une idée répandue, mais un outil décidé au plus près du terrain.
        </p>
      </ContentSection>

      <ContentSection id="soutiens-oppositions" n={5} title="Qui soutient, qui s'y oppose">
        <p>
          Les partisans de la réforme se trouvent surtout du côté des employeurs et des secteurs en tension. Pour eux, la fin du test
          du marché du travail supprime une lourdeur qui faisait fuir les candidats et ralentissait les embauches. Les organisations
          patronales rappellent que les étrangers occupent souvent des postes que les Polonais délaissent, du soin à domicile à
          l'hôtellerie.
        </p>
        <p>
          Les réserves viennent d'horizons variés. Une partie de l'opinion craint une pression sur les salaires et sur les services
          publics. À droite, et en particulier dans la mouvance qui soutient le président, on insiste sur la priorité nationale et la
          maîtrise des flux. Le débat n'oppose pourtant pas un camp de l'ouverture totale à un camp de la fermeture. La réalité est
          plus nuancée. Le gouvernement de Donald Tusk, à l'origine de la réforme, a lui-même resserré les contrôles et multiplié les
          refus, comme le montrent les statistiques officielles. La ligne de partage passe donc moins entre ouverture et fermeture
          qu'entre deux façons de gérer une immigration devenue indispensable.
        </p>
      </ContentSection>

      <ContentSection id="contexte-politique" n={6} title="Le contexte politique : une cohabitation tendue">
        <p>
          La réforme arrive dans un climat de cohabitation difficile. La loi a été votée par le Parlement en mars 2025 puis signée le
          10 avril 2025 par le président de l'époque, Andrzej Duda. Quelques mois plus tard, en août 2025, Karol Nawrocki lui a
          succédé. Soutenu par le parti Droit et justice, eurosceptique et ferme sur l'immigration, il a fait du veto un instrument
          régulier face au gouvernement pro-européen de Donald Tusk.
        </p>
        <p>
          Une précision s'impose pour éviter les raccourcis. Cette loi n'est pas l'œuvre de Nawrocki, et il n'avait pas le pouvoir de
          la bloquer puisqu'elle était déjà promulguée à son arrivée. Son influence porte sur la suite, c'est-à-dire sur les futures
          évolutions du droit et sur le ton du débat. Sa fermeté s'est manifestée ailleurs, par exemple lorsqu'il a opposé son veto,
          fin août 2025, à une loi sur l'aide aux réfugiés ukrainiens. Ce dossier, distinct de la réforme du travail, montre la
          pression qui pèse sur toute la politique migratoire du pays.
        </p>
        <p>
          Cette fermeté se retrouve sur un autre dossier européen, le refus de la relocalisation des demandeurs d'asile, que nous
          traitons dans notre article{" "}
          <Link to={lp("/blog/pacte-migratoire-pologne")} className={INTERNAL_LINK}>
            Pacte migratoire de l'UE : pourquoi la Pologne refuse la relocalisation
          </Link>
          .
        </p>
      </ContentSection>

      <ContentSection id="contexte-geopolitique" n={7} title="Le contexte géopolitique">
        <p>
          Impossible de comprendre cette loi sans regarder la carte. La Pologne est devenue, en une génération, un pays d'immigration
          après avoir longtemps été un pays de départ. Sa main-d'œuvre étrangère repose d'abord sur les Ukrainiens. Fin juillet 2025,
          ils étaient environ 741 000 à travailler légalement, soit les deux tiers de l'ensemble des travailleurs étrangers, les
          Biélorusses venant loin derrière.
        </p>
        <p>
          La guerre en Ukraine pèse sur tout. Elle a amené des centaines de milliers de personnes sur le marché polonais, et la
          moindre incertitude sur leur statut inquiète les entreprises. À l'échelle européenne, Varsovie résiste par ailleurs au
          volet contraignant du pacte migratoire de l'Union, sur une ligne où le gouvernement et la présidence se rejoignent en
          partie. La concurrence régionale pour attirer la main-d'œuvre, avec l'Allemagne et la Tchéquie notamment, ajoute une
          pression supplémentaire.
        </p>
      </ContentSection>

      <ContentSection id="consequences" n={8} title="Les conséquences déjà visibles">
        <p>
          Premier effet, un record. Fin 2025, près de 1,29 million d'étrangers travaillaient légalement en Pologne et cotisaient à la
          sécurité sociale, soit 8 % de plus qu'un an plus tôt. Les étrangers représentent désormais autour de 7 % des personnes en
          emploi. Si l'on compte tous les résidents étrangers, et pas seulement les actifs déclarés, les estimations vont de deux à
          deux millions et demi de personnes.
        </p>
        <p>
          Deuxième effet, plus surprenant. Le nombre de nouveaux permis de travail a baissé. Au premier semestre 2025, les voïvodes
          ont délivré près de 145 000 permis, soit 17 % de moins qu'un an plus tôt, et les refus ont augmenté de près de 30 %. La
          baisse ne traduit pas un désintérêt, mais le durcissement des vérifications destiné à éliminer les permis frauduleux. Moins
          de papiers, mais des dossiers plus solides.
        </p>
        <p>
          Troisième effet, une diversification rapide des origines. Les Ukrainiens et les Biélorusses restent majoritaires, mais des
          communautés venues de plus loin progressent fortement, de la Colombie au Népal en passant par l'Inde et les Philippines.
          La contribution économique suit. Selon un rapport de l'Institut des affaires publiques mené avec Deloitte et Ipsos, les
          étrangers ont accru le produit intérieur brut polonais de 200 à 416 milliards de zlotys en 2025.
        </p>
      </ContentSection>

      <ContentSection id="pour-vous" n={9} title="Ce que cela change pour vous">
        <p>
          Si vous venez de l'UE, de l'EEE ou de Suisse, rien ne change sur le fond. Vous gardez le libre accès au marché du travail
          et vous suivez la procédure d'enregistrement de séjour décrite sur notre page{" "}
          <Link to={lp("/demarches")} className={INTERNAL_LINK}>Démarches</Link>.
        </p>
        <p>
          Si vous êtes britannique installé avant 2021, vous restez couvert par l'accord de retrait et conservez vos droits. Si vous
          arrivez du Royaume-Uni après cette date, vous êtes un ressortissant de pays tiers, comme les autres nationalités hors UE,
          et vous passez par un permis de travail puis un titre de séjour.
        </p>
        <p>
          Si vous venez d'un pays tiers, la bonne nouvelle est la rapidité retrouvée côté employeur. La contrepartie est un contrôle
          plus strict, un dossier qui doit être irréprochable et une rémunération au moins égale au minimum légal. Pour situer ce
          salaire dans le budget d'une vie en Pologne, voir{" "}
          <Link to={lp("/cout-de-la-vie")} className={INTERNAL_LINK}>Coût de la vie</Link>.
        </p>
        <p>
          Pour le parcours détaillé, du visa à la carte de séjour, voir notre guide{" "}
          <Link to={lp("/demarches-pays-tiers")} className={INTERNAL_LINK}>
            S'installer en Pologne quand on vient d'un pays tiers
          </Link>
          .
        </p>
      </ContentSection>

      <ContentSection id="et-apres" n={10} title="Et après">
        <p>
          La réforme est installée, mais le débat n'est pas clos. La cohabitation entre un gouvernement réformateur et une présidence
          ferme sur l'immigration promet d'autres bras de fer, avec les élections législatives de 2027 en ligne de mire. La question
          des métiers protégés et des éventuels plafonds reviendra à chaque soubresaut du marché de l'emploi. Une chose paraît solide
          pour l'instant. Le besoin de main-d'œuvre étrangère ne faiblit pas.
        </p>
      </ContentSection>

      {/* Sources */}
      <Sources sources={SOURCES} testid="blog-post-sources" />

      {/* Avertissement final, style cohérent avec Demarches et Entrepreneuriat */}
      <Warn testid="blog-post-warn-final">
        Cet article a une vocation d'information générale. Les chiffres et les règles évoluent, en particulier dans un contexte
        politique mouvant. Reportez-vous aux sources officielles citées ci-dessus à la date de votre lecture.
      </Warn>
    </article>
  );
}
